'use client';

import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import type {AssetVideo} from '@/generated/graphql';
import type {PreviewableLecture} from '@/types/lecture';
import {usePreviewableAssets} from './use-previewable-assets';

interface CoursePreviewVideoProviderProps {
    children: ReactNode;
}

interface CoursePreviewVideoContextValue {
    previewableItems: PreviewableLecture[];
    selectedId?: string;
    selectedAsset?: AssetVideo;
    setSelectedId: (id: string, callback?: () => void) => void;
}

const CoursePreviewVideoContext = createContext<CoursePreviewVideoContextValue | undefined>(
    undefined,
);

/**
 * Provider that manages previewable asset selection for course preview.
 * Uses a simple id→asset map — behaviour is identical for promo video and curriculum lectures.
 */
export function CoursePreviewVideoProvider({children}: CoursePreviewVideoProviderProps) {
    const previewableItems = usePreviewableAssets();

    const [selectedId, setSelectedIdState] = useState<string | undefined>(
        previewableItems[0]?.id,
    );

    const setSelectedId = useCallback((id: string, callback?: () => void) => {
        setSelectedIdState(id);
        callback?.();
    }, []);

    // When the previewable items list changes and nothing is selected, default to the first.
    React.useEffect(() => {
        if (previewableItems.length > 0 && !selectedId) {
            setSelectedIdState(previewableItems[0].id);
        }
    }, [previewableItems, selectedId]);

    const assetById = useMemo(() => {
        const map = new Map<string, AssetVideo>();
        previewableItems.forEach((item) => {
            if (item.asset) map.set(item.id, item.asset);
        });
        return map;
    }, [previewableItems]);

    const selectedAsset = selectedId ? assetById.get(selectedId) : undefined;

    const contextValue: CoursePreviewVideoContextValue = {
        previewableItems,
        selectedId,
        selectedAsset,
        setSelectedId,
    };

    return (
        <CoursePreviewVideoContext.Provider value={contextValue}>
            {children}
        </CoursePreviewVideoContext.Provider>
    );
}

/**
 * Hook to access the course preview video context — should only be used by the
 * hooks exported from this file to avoid breaking encapsulation.
 */
function useCoursePreviewVideoContext() {
    const context = useContext(CoursePreviewVideoContext);
    if (!context) {
        throw new Error(
            'useCoursePreviewVideoContext must be used within a CoursePreviewVideoProvider',
        );
    }
    return context;
}

/**
 * Hook to set the selected preview item by ID.
 */
export function useSetSelectedPreviewAssetId() {
    const {setSelectedId} = useCoursePreviewVideoContext();
    return setSelectedId;
}

/**
 * Hook to check if an ID is currently selected for preview.
 */
export function useIsCurrentPreview(id: string) {
    const {selectedId} = useCoursePreviewVideoContext();
    return selectedId === id;
}

/**
 * Hook to get the currently selected asset for rendering in the media player.
 */
export function useSelectedPreviewAsset() {
    const {selectedAsset} = useCoursePreviewVideoContext();
    return selectedAsset;
}

/**
 * Hook to check if we're in a fallback state where no previewable content exists.
 */
export function useIsUsingPromoVideoFallback() {
    const {previewableItems} = useCoursePreviewVideoContext();
    return previewableItems.length === 0;
}

/**
 * Hook to get all previewable items for display in the preview list.
 * Promo video (if available) appears first, followed by curriculum lectures.
 * The asset field on each item is populated lazily once query resolves.
 */
export function usePreviewableLectures(): PreviewableLecture[] {
    const {previewableItems} = useCoursePreviewVideoContext();
    return previewableItems;
}
